import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PlatformPage } from './pages/PlatformPage';
import { useSession } from './session';
import { AppShell } from './components/AppShell';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import { SchedulePage } from './pages/SchedulePage';
import { NonWorkingDaysPage } from './pages/NonWorkingDaysPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { JobDetailPage } from './pages/JobDetailPage';
import { JobsPage } from './pages/JobsPage';
import { NewJobPage } from './pages/NewJobPage';
import { QuotesPage } from './pages/QuotesPage';
import { NewQuotePage } from './pages/NewQuotePage';
import { QuoteDetailPage } from './pages/QuoteDetailPage';
import { QuoteSettingsPage } from './pages/QuoteSettingsPage';
import { QuoteReportsPage } from './pages/QuoteReportsPage';
import { TimesheetsPage } from './pages/TimesheetsPage';
import { TimesheetDetailPage } from './pages/TimesheetDetailPage';
import { TimesheetExportPage } from './pages/TimesheetExportPage';
import { TimesheetSettingsPage } from './pages/TimesheetSettingsPage';
import { LibraryPage } from './pages/LibraryPage';
import { LoginPage } from './pages/LoginPage';
import { MorePage } from './pages/MorePage';
import { MySchedulePage } from './pages/MySchedulePage';
import { MyRecordPage } from './pages/MyRecordPage';
import { CompetencyCodesPage } from './pages/CompetencyCodesPage';
import { InductionCodesPage } from './pages/InductionCodesPage';
import { MachineTypesPage } from './pages/MachineTypesPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { BillingPage } from './pages/BillingPage';
import { UsersPage } from './pages/UsersPage';
import { RecentlyDeletedPage } from './pages/RecentlyDeletedPage';
import { CompanySettingsPage } from './pages/CompanySettingsPage';
import { SettingsIndexRedirect, SettingsLayout } from './components/SettingsLayout';
import { PostDetailPage } from './pages/PostDetailPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ResourceDetailPage } from './pages/ResourceDetailPage';
import { ResourcesHomePage } from './pages/ResourcesHomePage';
import { BulkReadingsPage } from './pages/BulkReadingsPage';
import { TemplateBuilderPage } from './pages/TemplateBuilderPage';
import { TemplateListPage } from './pages/TemplateListPage';
import { FormFillPage } from './pages/FormFillPage';
import { FormsHomePage } from './pages/FormsHomePage';
import { SubmissionDetailPage, SubmissionListPage } from './pages/SubmissionsPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { RESOURCE_GROUPS } from './lib/resourceGroups';
import { SignupPage } from './pages/SignupPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { SecurityPage } from './pages/SecurityPage';
import { EnrolTwoFactorPage } from './pages/EnrolTwoFactorPage';
import { ConfirmEmailChangePage } from './pages/ConfirmEmailChangePage';
import {
  CancelTwoFactorResetPage,
  CompleteTwoFactorResetPage,
  LostTwoFactorPage,
} from './pages/LostTwoFactorPage';

/**
 * Waits for the first session check before deciding anything. Without the wait,
 * a signed-in person reloading the page gets bounced to the login screen for a
 * moment before being sent back — which looks broken even though it isn't.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, user } = useSession();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { loading, user } = useSession();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/**
 * Guards the manager screens.
 *
 * A field worker who types /app/jobs is sent back to their own schedule rather
 * than shown an error — they have not done anything wrong, that page is simply
 * not part of their product. The server refuses these routes independently;
 * this is only about not showing someone a broken page.
 */
function ManagersOnly({ children }: { children: React.ReactNode }) {
  const { loading, user } = useSession();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'field') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function AdminsOnly({ children }: { children: React.ReactNode }) {
  const { loading, user } = useSession();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/**
 * A post's old URL, carrying its id across the round-37 rename.
 *
 * `replace`, so Back goes where the reader came from rather than bouncing
 * them through the redirect again.
 */
function PostRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/announcements/${id ?? ''}`} replace />;
}

/** Managers get the dashboard; field workers get their assignments. */
function Home() {
  const { user } = useSession();
  if (!user) return null;
  return user.role === 'field' ? <MySchedulePage /> : <HomePage />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />

      <Route
        path="/signup"
        element={
          <PublicOnly>
            <SignupPage />
          </PublicOnly>
        }
      />
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* The break-glass path, for someone who has lost the phone AND the
          recovery codes. Public, because the person using it cannot sign in —
          which is the entire point (docs/spec/11-account-security.md §2). */}
      <Route path="/lost-2fa" element={<LostTwoFactorPage />} />

      {/* These are all reached from an email link and must work whether or
          not there is already a session in the browser. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/cancel-2fa-reset" element={<CancelTwoFactorResetPage />} />
      <Route path="/complete-2fa-reset" element={<CompleteTwoFactorResetPage />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />

      {/* Signed in, but deliberately OUTSIDE the app shell: an admin who has
          not enrolled is refused by every route the shell would try to load,
          so rendering the shell around this would fill the screen with errors
          about the one thing they cannot yet do anything about. */}
      <Route
        path="/enrol-2fa"
        element={
          <RequireAuth>
            <EnrolTwoFactorPage />
          </RequireAuth>
        }
      />

      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route
          path="calendar"
          element={
            <ManagersOnly>
              <SchedulePage />
            </ManagersOnly>
          }
        />
        <Route
          path="calendar/non-working-days"
          element={
            <ManagersOnly>
              <NonWorkingDaysPage />
            </ManagersOnly>
          }
        />
        {/* Round 43. Managers only, like jobs. A company with Quotes off
            still reaches these URLs, and the server answers 404, which the
            pages show as a plain "not found" rather than a broken screen. */}
        <Route
          path="quotes"
          element={
            <ManagersOnly>
              <QuotesPage />
            </ManagersOnly>
          }
        />
        {/* Ahead of `quotes/:id`, so "reports" is never read as a quote id. */}
        <Route
          path="quotes/reports"
          element={
            <AdminsOnly>
              <QuoteReportsPage />
            </AdminsOnly>
          }
        />
        <Route
          path="quotes/new"
          element={
            <ManagersOnly>
              <NewQuotePage />
            </ManagersOnly>
          }
        />
        <Route
          path="quotes/:id"
          element={
            <ManagersOnly>
              <QuoteDetailPage />
            </ManagersOnly>
          }
        />
        {/* Round 44. Open to every role: a field worker's own week lives
            here, and the server answers 404 for anybody else's. The export
            is the office's, and comes BEFORE timesheets/:id so the word is
            never read as an id. */}
        <Route path="timesheets" element={<TimesheetsPage />} />
        <Route
          path="timesheets/export"
          element={
            <ManagersOnly>
              <TimesheetExportPage />
            </ManagersOnly>
          }
        />
        <Route path="timesheets/:id" element={<TimesheetDetailPage />} />
        <Route
          path="jobs"
          element={
            <ManagersOnly>
              <JobsPage />
            </ManagersOnly>
          }
        />
        {/* Ahead of `jobs/:id`, so "new" is never read as a job id. */}
        <Route
          path="jobs/new"
          element={
            <ManagersOnly>
              <NewJobPage />
            </ManagersOnly>
          }
        />
        <Route
          path="jobs/:id"
          element={
            <ManagersOnly>
              <JobDetailPage />
            </ManagersOnly>
          }
        />
        <Route
          path="resources"
          element={
            <ManagersOnly>
              <ResourcesHomePage />
            </ManagersOnly>
          }
        />
        {/* Ahead of the group lists and of `resources/:id`, so "readings" is
            never read as a resource id. */}
        <Route
          path="resources/readings"
          element={
            <ManagersOnly>
              <BulkReadingsPage />
            </ManagersOnly>
          }
        />
        {/* The two lookup lists moved to the Settings tabs in round 30; these
            redirects keep old bookmarks and muscle memory working. */}
        <Route
          path="resources/competency-codes"
          element={<Navigate to="/app/settings/competency-codes" replace />}
        />
        <Route
          path="resources/machine-types"
          element={<Navigate to="/app/settings/machine-types" replace />}
        />
        {/* The group lists, one per card on the landing page. These are static
            segments and `resources/:id` below is dynamic, so React Router ranks
            them first — and an id is a uuid, which can never collide with one
            of these words anyway. */}
        {RESOURCE_GROUPS.map((group) => (
          <Route
            key={group.key}
            path={`resources/${group.path}`}
            element={
              <ManagersOnly>
                <ResourcesPage group={group.key} />
              </ManagersOnly>
            }
          />
        ))}
        <Route
          path="resources/:id"
          element={
            <ManagersOnly>
              <ResourceDetailPage />
            </ManagersOnly>
          }
        />
        <Route
          path="clients"
          element={
            <ManagersOnly>
              <ClientsPage />
            </ManagersOnly>
          }
        />
        <Route
          path="clients/:id"
          element={
            <ManagersOnly>
              <ClientDetailPage />
            </ManagersOnly>
          }
        />
        {/* The Library and Notifications are the tabs every role gets. */}
        {/* Forms. Filling one in is open to EVERY role — the round-16
            carve-out extension — while building templates is manager+. That
            is why these are separate routes rather than one guarded subtree.
            The fill routes are declared first so `forms/templates` cannot be
            shadowed by anything.

            Round 28 reshaped the section to ZT's: the landing lists the forms
            themselves (one tap from the phone tab to a prestart), Submissions
            is its own page opening on a per-form overview. `forms/fill`
            WITHOUT a template id stays mounted: it owns `?draft=` — the
            continuation URL every unfinished form, linked panel and bookmark
            already carries. */}
        <Route path="forms" element={<FormsHomePage />} />
        <Route path="forms/fill" element={<FormFillPage />} />
        <Route path="forms/fill/:templateId" element={<FormFillPage />} />
        <Route path="forms/submissions" element={<SubmissionListPage />} />
        <Route path="forms/submissions/:id" element={<SubmissionDetailPage />} />
        <Route
          path="forms/templates"
          element={
            <ManagersOnly>
              <TemplateListPage />
            </ManagersOnly>
          }
        />
        <Route
          path="forms/templates/:id"
          element={
            <ManagersOnly>
              <TemplateBuilderPage />
            </ManagersOnly>
          }
        />

        <Route path="library" element={<LibraryPage />} />
        {/* Every role, and gated by no setting: it shows the caller their own
            record and nothing else, self-scoped on the server by the account
            the request arrives on (round 27). */}
        <Route path="my-record" element={<MyRecordPage />} />
        {/* The phone tab bar's overflow (DM1). Every role. */}
        <Route path="more" element={<MorePage />} />
        {/* The human feed, renamed in round 37 — every role, because reading
            and acknowledging a safety post is a field worker's job. */}
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="announcements/:id" element={<PostDetailPage />} />
        {/* The system feed, new in round 37 and manager+: it names people
            beside their expiring tickets and their birthdays. */}
        <Route
          path="notifications"
          element={
            <ManagersOnly>
              <NotificationsPage />
            </ManagersOnly>
          }
        />
        {/* A post's old URL still works — bookmarks and four months of muscle
            memory point at it (round 30's redirect precedent). The BARE
            /app/notifications deliberately does not redirect: it now means
            the other feed, which is the one thing about this rename that a
            returning user has to learn. No email links there. */}
        <Route path="notifications/:id" element={<PostRedirect />} />
        {/* The old user-management URL. "People" became the Users tab under
            Settings in round 30. */}
        <Route path="people" element={<Navigate to="/app/settings/users" replace />} />

        {/* The owner console (round 32). No guard component around it: the
            server answers 404 to everyone without the platform flag, and a
            client-side gate on a screen whose data is already refused would be
            a second place for the rule to live. */}
        <Route path="platform" element={<PlatformPage />} />

        {/* Settings (round 30): one frame, a tab per section. The layout is
            navigation only — every section keeps its own guard, because a tab
            strip is not an access control. */}
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<SettingsIndexRedirect />} />
          <Route
            path="company"
            element={
              <AdminsOnly>
                <CompanySettingsPage />
              </AdminsOnly>
            }
          />
          <Route
            path="users"
            element={
              <AdminsOnly>
                <UsersPage />
              </AdminsOnly>
            }
          />
          <Route
            path="billing"
            element={
              <AdminsOnly>
                <BillingPage />
              </AdminsOnly>
            }
          />
          <Route
            path="quotes"
            element={
              <AdminsOnly>
                <QuoteSettingsPage />
              </AdminsOnly>
            }
          />
          <Route
            path="timesheets"
            element={
              <AdminsOnly>
                <TimesheetSettingsPage />
              </AdminsOnly>
            }
          />
          {/* Manager-level, deliberately: whoever runs the crew is who knows
              what the tickets are called, and an admin-only door would put
              these lists out of reach of the people who maintain them
              (rounds 27 and 28 — unchanged by the round-30 move here). */}
          <Route
            path="competency-codes"
            element={
              <ManagersOnly>
                <CompetencyCodesPage />
              </ManagersOnly>
            }
          />
          <Route
            path="induction-codes"
            element={
              <ManagersOnly>
                <InductionCodesPage />
              </ManagersOnly>
            }
          />
          <Route
            path="machine-types"
            element={
              <ManagersOnly>
                <MachineTypesPage />
              </ManagersOnly>
            }
          />
          <Route
            path="deleted"
            element={
              <AdminsOnly>
                <RecentlyDeletedPage />
              </AdminsOnly>
            }
          />
          {/* Every role, unlike billing: a field worker has a password to
              change and may turn on two-factor if they want it. */}
          <Route path="security" element={<SecurityPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
